import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { RichEditor } from '@odo/shared-ui';
import type { RichEditorImageUploadHandler } from '@odo/shared-ui';
import type { CommunicationChannel } from '../../../../services/communications';
import { uploadPracticeImage } from '../../../../services/practiceImages';
import { BodySeedChips } from './BodySeedChips';
import { ChannelSegmentedControl } from './ChannelSegmentedControl';
import type { CommunicationFormValues } from './communicationFormValues';
import { EntryBodyCharCount } from './EntryBodyCharCount';
import { EntryFieldsSection } from './EntryFieldsSection';
import { ReferencePill } from './ReferencePill';
import { TitleInput } from './TitleInput';

interface CommunicationFormMainProps {
    practiceId: string | null;
    form: CommunicationFormValues;
    setForm: Dispatch<SetStateAction<CommunicationFormValues>>;
    onChannelChange: (channel: CommunicationChannel) => void;
    plainBodyLength: number;
    missingBodyText: boolean;
    missingBodyHintId: string;
    disabled: boolean;
    durationError: string | null;
    timerIsRunning: boolean;
    timerElapsedLabel: string;
    onToggleTimer: () => void;
}

// Two stacked cards: thread identity above the entry being written. The
// entry card's chrome deliberately matches EntryComposer.tsx (the detail
// page's composer) so both surfaces read as one experience.
export function CommunicationFormMain({
    practiceId,
    form,
    setForm,
    onChannelChange,
    plainBodyLength,
    missingBodyText,
    missingBodyHintId,
    disabled,
    durationError,
    timerIsRunning,
    timerElapsedLabel,
    onToggleTimer
}: CommunicationFormMainProps) {
    // Only the fileId is persisted - the signed url expires. Image upload
    // needs a practice (practice-scoped storage), so it's disabled until one's picked.
    const handleImageUpload: RichEditorImageUploadHandler = useCallback(
        async (file) => {
            if (!practiceId) {
                throw new Error('Select a practice before adding images.');
            }
            const { url, fileId } = await uploadPracticeImage(practiceId, file, 'call');
            return { url, fileId };
        },
        [practiceId]
    );

    const setEntry: Dispatch<SetStateAction<CommunicationFormValues['entry']>> = (value) =>
        setForm((f) => ({
            ...f,
            entry:
                typeof value === 'function'
                    ? (
                          value as (
                              prev: CommunicationFormValues['entry']
                          ) => CommunicationFormValues['entry']
                      )(f.entry)
                    : value
        }));

    return (
        <div className="flex-1 min-w-0 flex flex-col gap-4">
            {/* Thread identity band: title is the headline, reference is the
                thing read aloud, so both sit above the entry rather than
                inside it. */}
            <div className="shrink-0 bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.04)] px-5 py-4 flex items-start justify-between gap-6">
                <div className="min-w-0 flex-1">
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em]">
                        New communication
                    </span>
                    <TitleInput
                        value={form.title}
                        onChange={(title) => setForm((f) => ({ ...f, title }))}
                        disabled={disabled}
                    />
                </div>
                <div className="shrink-0 flex flex-col items-end">
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em]">
                        Reference
                    </span>
                    <ReferencePill referenceNo={form.referenceNo} className="mt-1.5" />
                </div>
            </div>

            {/* The entry - same chrome as the detail page's composer
                (EntryComposer.tsx): a border marks it as the thing actively
                being written, channel tiles lead the header block, and the
                body sits in the same padded, bordered, focus-ringed box.
                Only the sizing rule differs (flex-1 min-h-0 vs. the
                composer's fixed min-h-[280px]): this page's column has a
                real viewport height to fill; the composer sits in a
                scrolling list's normal document flow instead. */}
            <div className="flex-1 min-h-0 bg-white border border-gray-400 rounded-xl flex flex-col overflow-hidden">
                <div className="px-5 pt-4 pb-3.5 border-b border-gray-100 flex flex-col gap-3.5">
                    <ChannelSegmentedControl
                        value={form.entry.channel}
                        onChange={onChannelChange}
                        disabled={disabled}
                    />

                    <EntryFieldsSection
                        values={form.entry}
                        setValues={setEntry}
                        disabled={disabled}
                        durationError={durationError}
                        timerIsRunning={timerIsRunning}
                        timerElapsedLabel={timerElapsedLabel}
                        onToggleTimer={onToggleTimer}
                    />
                </div>

                <div className="px-5 py-4 flex-1 min-h-0 flex flex-col">
                    <div
                        className={`border rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col focus-within:border-gray-400 transition-colors ${
                            missingBodyText ? 'border-red-300' : 'border-gray-200'
                        }`}
                    >
                        <RichEditor
                            value={form.entry.doc}
                            onChange={(doc) => setEntry((entry) => ({ ...entry, doc }))}
                            onImageUpload={handleImageUpload}
                            placeholder="What did they reach out about? What did you do?"
                            chrome="flush"
                            readOnly={disabled}
                            toolbarRightSlot={<EntryBodyCharCount length={plainBodyLength} />}
                            belowToolbarSlot={
                                <BodySeedChips
                                    doc={form.entry.doc}
                                    channel={form.entry.channel}
                                    onInsert={(doc) => setEntry((entry) => ({ ...entry, doc }))}
                                    disabled={disabled}
                                />
                            }
                        />
                    </div>
                    {missingBodyText && (
                        <p
                            id={missingBodyHintId}
                            role="alert"
                            className="text-[11px] text-red-500 mt-1.5 shrink-0"
                        >
                            Add a note before saving
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
